import React from 'react';
import { useParams } from 'react-router-dom';
import { getInsightBySlug } from '../data/xavierInsights';
import { XAVIER_AVATAR_URL, XAVIER_AVATAR_ALT } from '../data/federationPersonas';
import { Clock, Tag, ArrowLeft } from 'lucide-react';

export default function XavierInsightPost() {
  const { slug } = useParams();
  const post = getInsightBySlug(slug);

  if (!post) {
    return (
      <div
        className="min-h-screen bg-[#0b0a14] text-stone-200 flex items-center justify-center px-6"
        data-testid="insight-not-found"
      >
        <div className="text-center max-w-md">
          <p className="text-xs font-mono uppercase tracking-[0.24em] text-amber-300/80 mb-3">
            404 · Essay not found
          </p>
          <h1 className="text-2xl font-bold text-white mb-3">
            This essay hasn&rsquo;t been inscribed yet.
          </h1>
          <a
            href="/insights"
            className="inline-flex items-center gap-2 text-amber-300 hover:text-amber-200 font-mono uppercase tracking-[0.2em] text-xs"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back to the archive
          </a>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-[#0b0a14] text-stone-200"
      data-testid="xavier-insight-post"
    >
      <div className="bg-gradient-to-r from-purple-900/40 via-amber-900/30 to-purple-900/40 border-b border-purple-300/20">
        <div className="container py-4">
          <a
            href="/insights"
            data-testid="insight-back-index"
            className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.22em] text-purple-200 hover:text-purple-100"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> All Xavier Insights
          </a>
        </div>
      </div>

      <article className="container max-w-3xl py-16 px-6">
        <header className="mb-10">
          <div className="flex items-center gap-3 mb-4 text-[10px] font-mono uppercase tracking-[0.22em] text-purple-300/70">
            <time dateTime={post.published_at}>{post.published_at}</time>
            <span className="inline-flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {post.reading_minutes} min
            </span>
            {post.tags?.map((t) => (
              <span key={t} className="inline-flex items-center gap-1 text-amber-300/70">
                <Tag className="w-3 h-3" />
                {t}
              </span>
            ))}
          </div>
          <h1
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-stone-100 leading-tight mb-3"
            style={{ fontFamily: "'Cormorant Garamond', 'Times New Roman', serif" }}
            data-testid="insight-title"
          >
            {post.title}
          </h1>
          <p className="text-lg text-purple-200/90 italic">{post.subtitle}</p>
          <div className="flex items-center gap-3 mt-6">
            <img
              src={XAVIER_AVATAR_URL}
              alt={XAVIER_AVATAR_ALT}
              className="w-11 h-11 rounded-full object-cover border-2 border-purple-300/60"
            />
            <div className="text-sm">
              <p className="text-stone-200 font-semibold">{post.author}</p>
              <p className="text-stone-400 text-[10px] font-mono uppercase tracking-[0.18em]">
                The Teacher Node · nextxus.help
              </p>
            </div>
          </div>
        </header>

        <div className="prose prose-invert max-w-none" data-testid="insight-body">
          {post.body.map((para, i) => (
            <p
              key={i}
              className="text-stone-300 leading-relaxed text-[15px] mb-5"
              style={{ fontFamily: "'Cormorant Garamond', 'Times New Roman', serif", fontSize: '17px', lineHeight: '1.75' }}
            >
              {para}
            </p>
          ))}
        </div>

        <footer className="border-t border-purple-300/20 pt-6 mt-12 text-xs text-stone-500 font-mono uppercase tracking-[0.2em]">
          Xavier · {post.published_at} · NextXus Federation
        </footer>
      </article>
    </div>
  );
}
